import { useMemo, useState } from "react";

import { ChevronDown, ChevronRight, Search } from "lucide-react";

import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";

import { useWorkingDays } from "../hooks/useWorkingDays";
import {
  type ClientGoalRow,
  type SellerMonthlyGoals,
  computeAnalysisMetrics,
} from "../types/salesGoals";
import MonthlyAnalysisHeader from "./MonthlyAnalysisHeader";

interface ClientAnalysisTableProps {
  clients: ClientGoalRow[];
  sellers?: SellerMonthlyGoals[];
  selectedMonth: number;
  year: number;
  groupBy: "seller" | "channel";
  isLoading: boolean;
}

function formatCLP(amount: number): string {
  return "$" + Math.round(amount).toLocaleString("es-CL");
}

function formatPct(pct: number): string {
  return `${Math.round(pct)}%`;
}

function pctColor(pct: number): string {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 50) return "text-orange-500";
  return "text-red-500";
}

export default function ClientAnalysisTable({
  clients,
  sellers,
  selectedMonth,
  year,
  groupBy,
  isLoading,
}: ClientAnalysisTableProps) {
  const wd = useWorkingDays(year, selectedMonth);
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const showVendedorCol = groupBy === "channel";
  const showFilter = groupBy === "seller";

  const filterOptions = useMemo(() => {
    if (!showFilter || !sellers) return [];
    return [...new Set(sellers.map((s) => s.seller_name))].sort();
  }, [sellers, showFilter]);

  const enriched = useMemo(() => {
    let filtered = clients;
    if (showFilter && selectedFilter !== "all") {
      filtered = clients.filter((c) => c.seller_name === selectedFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((c) =>
        c.client_name.toLowerCase().includes(q)
      );
    }

    return filtered
      .map((c) => {
        const projection = c.months[selectedMonth] || 0;
        const sales = c.actuals[selectedMonth] || 0;
        const metrics = computeAnalysisMetrics(
          projection,
          sales,
          wd.totalWorkingDays,
          wd.elapsedWorkingDays,
          wd.remainingWorkingDays
        );
        return { ...c, projection, sales, ...metrics };
      })
      .filter((r) => r.projection > 0 || r.sales > 0);
  }, [clients, selectedMonth, selectedFilter, showFilter, searchQuery, wd]);

  const grouped = useMemo(() => {
    const groups: Record<string, typeof enriched> = {};
    for (const r of enriched) {
      const key =
        groupBy === "seller"
          ? r.seller_name || "Sin Vendedor"
          : r.sales_channel_name || "Sin Canal";
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return groups;
  }, [enriched, groupBy]);

  if (isLoading) {
    return <TableSkeleton rows={8} columns={9} />;
  }

  const grandTotals = enriched.reduce(
    (acc, r) => ({
      projection: acc.projection + r.projection,
      sales: acc.sales + r.sales,
      expectedToday: acc.expectedToday + r.expectedToday,
      estimatedClose: acc.estimatedClose + r.estimatedClose,
    }),
    { projection: 0, sales: 0, expectedToday: 0, estimatedClose: 0 }
  );
  const grandPct =
    grandTotals.expectedToday > 0
      ? (grandTotals.sales / grandTotals.expectedToday) * 100
      : 0;
  const grandEstDaily =
    wd.totalWorkingDays > 0 ? grandTotals.projection / wd.totalWorkingDays : 0;
  const grandActDaily =
    wd.elapsedWorkingDays > 0 ? grandTotals.sales / wd.elapsedWorkingDays : 0;

  const colSpan = showVendedorCol ? 9 : 8;
  const cellBorder = "border-r border-border";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        {showFilter && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-muted-foreground">
              Vendedor:
            </span>
            <Select value={selectedFilter} onValueChange={setSelectedFilter}>
              <SelectTrigger className="h-9 w-full sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filterOptions.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full pl-9"
          />
        </div>
      </div>

      <MonthlyAnalysisHeader
        month={selectedMonth}
        year={year}
        workingDays={wd}
      />

      <div className="w-full min-w-0 overflow-x-auto rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className={`sticky left-0 z-10 min-w-[200px] whitespace-nowrap bg-card ${cellBorder}`}
              >
                Cliente
              </TableHead>
              {showVendedorCol && (
                <TableHead
                  className={`min-w-[130px] whitespace-nowrap ${cellBorder}`}
                >
                  Vendedor
                </TableHead>
              )}
              <TableHead
                className={`min-w-[130px] whitespace-nowrap text-right ${cellBorder}`}
              >
                Proyección
              </TableHead>
              <TableHead
                className={`min-w-[130px] whitespace-nowrap text-right ${cellBorder}`}
              >
                Venta
              </TableHead>
              <TableHead
                className={`min-w-[150px] whitespace-nowrap text-right ${cellBorder}`}
              >
                Debería Llevar
              </TableHead>
              <TableHead
                className={`min-w-[70px] whitespace-nowrap text-right ${cellBorder}`}
              >
                %
              </TableHead>
              <TableHead
                className={`min-w-[140px] whitespace-nowrap text-right ${cellBorder}`}
              >
                Cierre Estimado
              </TableHead>
              <TableHead
                className={`min-w-[130px] whitespace-nowrap text-right ${cellBorder}`}
              >
                Est. Vta Diaria
              </TableHead>
              <TableHead className="min-w-[130px] whitespace-nowrap text-right">
                Real Vta Diaria
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(grouped).map(([groupName, groupClients], index) => {
              const isExpanded = expandedGroups.has(groupName);
              const sub = groupClients.reduce(
                (acc, r) => ({
                  projection: acc.projection + r.projection,
                  sales: acc.sales + r.sales,
                  expectedToday: acc.expectedToday + r.expectedToday,
                  estimatedClose: acc.estimatedClose + r.estimatedClose,
                }),
                {
                  projection: 0,
                  sales: 0,
                  expectedToday: 0,
                  estimatedClose: 0,
                }
              );
              const subPct =
                sub.expectedToday > 0
                  ? (sub.sales / sub.expectedToday) * 100
                  : 0;
              const subEstDaily =
                wd.totalWorkingDays > 0
                  ? sub.projection / wd.totalWorkingDays
                  : 0;
              const subActDaily =
                wd.elapsedWorkingDays > 0
                  ? sub.sales / wd.elapsedWorkingDays
                  : 0;

              return [
                <TableRow
                  key={`header-${groupName}`}
                  style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/70"
                  onClick={() => toggleGroup(groupName)}
                >
                  <TableCell
                    colSpan={colSpan}
                    className="sticky left-0 z-10 bg-muted/50 text-sm font-bold"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      {groupName} ({groupClients.length} clientes)
                    </div>
                  </TableCell>
                </TableRow>,
                ...(isExpanded
                  ? [
                      ...groupClients.map((r) => (
                        <TableRow key={r.client_id}>
                          <TableCell
                            className={`sticky left-0 z-10 bg-card pl-6 font-medium ${cellBorder}`}
                          >
                            {r.client_name}
                          </TableCell>
                          {showVendedorCol && (
                            <TableCell className={`text-sm ${cellBorder}`}>
                              {r.seller_name}
                            </TableCell>
                          )}
                          <TableCell
                            className={`text-right tabular-nums text-blue-600 ${cellBorder}`}
                          >
                            {formatCLP(r.projection)}
                          </TableCell>
                          <TableCell
                            className={`text-right tabular-nums text-red-500 ${cellBorder}`}
                          >
                            {formatCLP(r.sales)}
                          </TableCell>
                          <TableCell
                            className={`text-right tabular-nums ${cellBorder}`}
                          >
                            {wd.elapsedWorkingDays > 0
                              ? formatCLP(r.expectedToday)
                              : "-"}
                          </TableCell>
                          <TableCell
                            className={`text-right font-semibold tabular-nums ${cellBorder} ${
                              wd.elapsedWorkingDays > 0
                                ? pctColor(r.pctAchievement)
                                : ""
                            }`}
                          >
                            {wd.elapsedWorkingDays > 0
                              ? formatPct(r.pctAchievement)
                              : "-"}
                          </TableCell>
                          <TableCell
                            className={`text-right tabular-nums ${cellBorder}`}
                          >
                            {formatCLP(r.estimatedClose)}
                          </TableCell>
                          <TableCell
                            className={`text-right tabular-nums ${cellBorder}`}
                          >
                            {formatCLP(r.estimatedDaily)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {wd.elapsedWorkingDays > 0
                              ? formatCLP(r.actualDaily)
                              : "-"}
                          </TableCell>
                        </TableRow>
                      )),
                      <TableRow
                        key={`subtotal-${groupName}`}
                        className="border-b-2"
                      >
                        <TableCell
                          className={`sticky left-0 z-10 bg-muted/30 pl-6 text-xs font-semibold uppercase text-muted-foreground ${cellBorder}`}
                        >
                          Subtotal {groupName}
                        </TableCell>
                        {showVendedorCol && (
                          <TableCell className={`bg-muted/30 ${cellBorder}`} />
                        )}
                        <TableCell
                          className={`bg-muted/30 text-right font-semibold tabular-nums text-blue-600 ${cellBorder}`}
                        >
                          {formatCLP(sub.projection)}
                        </TableCell>
                        <TableCell
                          className={`bg-muted/30 text-right font-semibold tabular-nums text-red-500 ${cellBorder}`}
                        >
                          {formatCLP(sub.sales)}
                        </TableCell>
                        <TableCell
                          className={`bg-muted/30 text-right font-semibold tabular-nums ${cellBorder}`}
                        >
                          {wd.elapsedWorkingDays > 0
                            ? formatCLP(sub.expectedToday)
                            : "-"}
                        </TableCell>
                        <TableCell
                          className={`bg-muted/30 text-right font-semibold tabular-nums ${cellBorder} ${
                            wd.elapsedWorkingDays > 0 ? pctColor(subPct) : ""
                          }`}
                        >
                          {wd.elapsedWorkingDays > 0 ? formatPct(subPct) : "-"}
                        </TableCell>
                        <TableCell
                          className={`bg-muted/30 text-right font-semibold tabular-nums ${cellBorder}`}
                        >
                          {formatCLP(sub.estimatedClose)}
                        </TableCell>
                        <TableCell
                          className={`bg-muted/30 text-right font-semibold tabular-nums ${cellBorder}`}
                        >
                          {formatCLP(subEstDaily)}
                        </TableCell>
                        <TableCell className="bg-muted/30 text-right font-semibold tabular-nums">
                          {wd.elapsedWorkingDays > 0
                            ? formatCLP(subActDaily)
                            : "-"}
                        </TableCell>
                      </TableRow>,
                    ]
                  : []),
              ];
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                className={`sticky left-0 z-10 bg-muted font-bold ${cellBorder}`}
              >
                Total General
              </TableCell>
              {showVendedorCol && <TableCell className={cellBorder} />}
              <TableCell
                className={`text-right font-bold tabular-nums text-blue-600 ${cellBorder}`}
              >
                {formatCLP(grandTotals.projection)}
              </TableCell>
              <TableCell
                className={`text-right font-bold tabular-nums text-red-500 ${cellBorder}`}
              >
                {formatCLP(grandTotals.sales)}
              </TableCell>
              <TableCell
                className={`text-right font-bold tabular-nums ${cellBorder}`}
              >
                {wd.elapsedWorkingDays > 0
                  ? formatCLP(grandTotals.expectedToday)
                  : "-"}
              </TableCell>
              <TableCell
                className={`text-right font-bold tabular-nums ${cellBorder} ${
                  wd.elapsedWorkingDays > 0 ? pctColor(grandPct) : ""
                }`}
              >
                {wd.elapsedWorkingDays > 0 ? formatPct(grandPct) : "-"}
              </TableCell>
              <TableCell
                className={`text-right font-bold tabular-nums ${cellBorder}`}
              >
                {formatCLP(grandTotals.estimatedClose)}
              </TableCell>
              <TableCell
                className={`text-right font-bold tabular-nums ${cellBorder}`}
              >
                {formatCLP(grandEstDaily)}
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {wd.elapsedWorkingDays > 0 ? formatCLP(grandActDaily) : "-"}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
}
